// src/context/AuthContext.js
import { createContext, useState, useEffect, useCallback, useRef, useContext, useMemo } from "react";
import { authService } from "../services/authService";
import { permissionService } from "../services/permissionService";
import { jwtDecode } from "jwt-decode";
import routeRegistry from "../services/routeRegistry";
import accessService from "../services/accessService";
import { CATALOG_PATHS, PREVIEW_MESSAGE, PREVIEW_READY_MESSAGE, homeUrl, isAccessPreviewFrame, matchRoute, mostPermissive } from "../permissions/access";
import apiClient from "../services/api";
import activityService from "../services/activityService";
import { resetCardRegistry } from '../services/cardInitializer';
import logger from '../utils/logger';
import { safeSetItem } from '../utils/safeStorage';

// Storage keys
const AUTH_TOKEN_KEY = 'access_token';
const AUTH_REFRESH_KEY = 'refresh_token';
const USER_DATA_KEY = 'user_data';
const CART_NOTIFIED_KEY = 'cart_notification_sent';

export const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  // Core state
  const [user, setUser] = useState(null);
  const [token, setToken] = useState(null);
  const [refreshToken, setRefreshToken] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  // User role state
  const [userRoles, setUserRoles] = useState({
    isSuperuser: false,
    roles: []
  });

  // Permission state
  const [, setPermissionCache] = useState({});
  const [, setCardPermissions] = useState({});
  const [, setCardPermsLoading] = useState(true);
  const [routesLoaded, setRoutesLoaded] = useState(false);
  const [access, setAccess] = useState(null);
  const accessVersionRef = useRef(null);
  const accessRefreshTimer = useRef(null);
  const [preview, setPreview] = useState(null);

  // Refs
  const refreshingToken = useRef(false);
  const isInitialMount = useRef(true);

  // --- Fetch card permissions ---
  const cardPermsFetched = useRef(false);
  const fetchCardPermissions = useCallback(async (force = false) => {
    // Prevent duplicate fetches
    if (cardPermsFetched.current && !force) {
      logger.log("[AuthContext] Card permissions already fetched, skipping...");
      return;
    }
    
    logger.log("[AuthContext] Fetching card permissions...");
    setCardPermsLoading(true);
    try {
      const result = await permissionService.getCardPermissions();
      logger.log("[AuthContext] Card permissions result:", result);

      if (result.success && Array.isArray(result.data)) {
        const perms = result.data
          .filter(card => card && card.identifier)
          .reduce((acc, card) => {
            acc[card.identifier] = Array.isArray(card.roles) ? card.roles : [];
            return acc;
          }, {});
        setCardPermissions(perms);
        cardPermsFetched.current = true;
        logger.log("[AuthContext] Card permissions loaded:", Object.keys(perms).length, "cards");
      } else {
        console.error("[AuthContext] Failed to fetch card permissions:", result.error || 'Unknown error');
        setCardPermissions({});
      }
    } catch (error) {
      console.error("[AuthContext] Exception fetching card permissions:", error);
      setCardPermissions({});
    } finally {
      setCardPermsLoading(false);
    }
  }, []);

  // --- Clear permission caches ---
  const clearPermissionCache = useCallback(() => {
    logger.log("[AuthContext] Clearing all permission caches");
    setPermissionCache({});
    setCardPermissions({});
    setCardPermsLoading(true);
    setRoutesLoaded(false);
  }, []);

  // --- Group access: pages, dashboard cards and UI elements ---
  const fetchAccess = useCallback(async () => {
    try {
      const data = await accessService.getMine();
      if (!accessVersionRef.current) accessVersionRef.current = String(data.version);
      setAccess({
        version: data.version,
        pages: new Set(data.pages || []),
        cards: new Set(data.cards || []),
        elements: data.elements || {},
        home: data.home || null,
      });
      return true;
    } catch (error) {
      console.error("[AuthContext] Failed to load access:", error);
      return false;
    }
  }, []);

  const reloadAccess = useCallback(async () => {
    const [, routesResult] = await Promise.all([fetchAccess(), permissionService.getRoutes()]);
    if (routesResult?.success && routesResult.data) {
      routeRegistry.registerRoutes(routesResult.data);
    }
  }, [fetchAccess]);

  // Superuser-only: see the app with one group's pages, cards and elements. API data is not filtered.
  const startPreview = useCallback(async (groupId, groupName) => {
    const data = await accessService.previewGroup(groupId);
    setPreview({
      groupId,
      groupName,
      access: {
        version: data.version,
        pages: new Set(data.pages || []),
        cards: new Set(data.cards || []),
        elements: data.elements || {},
        home: data.home || null,
      },
    });
  }, []);
  const stopPreview = useCallback(() => setPreview(null), []);

  useEffect(() => {
    if (!isAccessPreviewFrame()) return undefined;
    const onMessage = (event) => {
      if (event.origin !== window.location.origin || event.data?.type !== PREVIEW_MESSAGE) return;
      const { groupId, groupName, access: draft } = event.data;
      setPreview({
        groupId,
        groupName,
        frame: true,
        access: {
          version: 'draft',
          pages: new Set(draft.pages || []),
          cards: new Set(draft.cards || []),
          elements: draft.elements || {},
          home: draft.home || null,
        },
      });
    };
    window.addEventListener('message', onMessage);
    window.parent.postMessage({ type: PREVIEW_READY_MESSAGE }, window.location.origin);
    return () => window.removeEventListener('message', onMessage);
  }, []);

  useEffect(() => {
    const interceptorId = apiClient.interceptors.response.use((response) => {
      const version = response?.headers?.['x-access-version'];
      if (version && accessVersionRef.current && version !== accessVersionRef.current) {
        accessVersionRef.current = version;
        clearTimeout(accessRefreshTimer.current);
        accessRefreshTimer.current = setTimeout(() => { reloadAccess(); }, 300);
      }
      return response;
    });
    return () => {
      apiClient.interceptors.response.eject(interceptorId);
      clearTimeout(accessRefreshTimer.current);
    };
  }, [reloadAccess]);

  // --- Token refresh ---
  const refreshTokenFunction = useCallback(async () => {
    if (refreshingToken.current) return false;
    refreshingToken.current = true;
    
    try {
      const storedRefresh = localStorage.getItem(AUTH_REFRESH_KEY);
      if (!storedRefresh) return false;
      
      const response = await apiClient.post('/api/token/refresh/', { refresh: storedRefresh });
      if (response.data?.access) {
        const newToken = response.data.access;
        safeSetItem(AUTH_TOKEN_KEY, newToken);
        setToken(newToken);
        
        if (response.data.refresh) {
          safeSetItem(AUTH_REFRESH_KEY, response.data.refresh);
          setRefreshToken(response.data.refresh);
        }
        
        setIsAuthenticated(true);
        return true;
      }
      return false;
    } catch (error) {
      console.error("Token refresh failed:", error);
      return false;
    } finally {
      refreshingToken.current = false;
    }
  }, []);

  // --- Refresh permissions ---
  const refreshPermissions = useCallback(async () => {
    if (!isAuthenticated) return false;
    logger.log("[AuthContext] Starting permission refresh");
    setPermissionCache({});
    await reloadAccess();
    return true;
  }, [isAuthenticated, reloadAccess]);

  // --- Cart notification ---
  const notifyCartSystem = useCallback((action, userId = null, previousUserId = null) => {
    const notificationKey = `${action}-${userId || previousUserId || 'unknown'}-${Date.now()}`;
    
    const lastNotification = localStorage.getItem(CART_NOTIFIED_KEY);
    if (lastNotification && Date.now() - parseInt(lastNotification.split('-').pop(), 10) < 5000) {
      logger.log('[AuthContext] Skipping cart notification - too soon');
      return;
    }
    
    logger.log(`[AuthContext] Sending cart notification: ${action}`);
    localStorage.setItem(CART_NOTIFIED_KEY, notificationKey);
    
    window.dispatchEvent(new CustomEvent('cart-auth-changed', {
      detail: { 
        isAuthenticated: action === 'login',
        userId: action === 'login' ? userId : null,
        previousUserId: action === 'logout' ? previousUserId : null,
        action
      }
    }));
  }, []);

  // --- Logout ---
  const handleLogout = useCallback(async () => {
    logger.log("[AuthContext] Logging out user");
    const currentRefreshToken = refreshToken || localStorage.getItem(AUTH_REFRESH_KEY);
    const previousUserId = user?.user_id || user?.id;

    // IMPORTANT: Sync cart to server BEFORE clearing auth tokens
    // This ensures cart data is saved while we still have valid credentials
    if (previousUserId) {
      logger.log("[AuthContext] Syncing cart before logout...");
      try {
        // Import syncService dynamically to avoid circular dependencies
        const { default: syncService } = await import('../services/SyncService');
        await syncService.handleLogout();
      } catch (error) {
        console.error("[AuthContext] Error syncing cart on logout:", error);
      }
    }

    // Clear all state
    setToken(null);
    setRefreshToken(null);
    setUser(null);
    setIsAuthenticated(false);
    setUserRoles({ isSuperuser: false, roles: [] });
    setPermissionCache({});
    setCardPermissions({});
    setCardPermsLoading(true);
    setRoutesLoaded(false);
    setAccess(null);
    setPreview(null);
    accessVersionRef.current = null;

    // Clear localStorage
    localStorage.removeItem(AUTH_TOKEN_KEY);
    localStorage.removeItem(AUTH_REFRESH_KEY);
    localStorage.removeItem(USER_DATA_KEY);

    // Clear registries
    if (routeRegistry?.clear) {
      routeRegistry.clear();
    }
    resetCardRegistry();

    // Reset fetch flags
    cardPermsFetched.current = false;

    // Call logout API
    if (navigator.onLine && currentRefreshToken) {
      authService.logout(currentRefreshToken).catch(console.error);
    }

    // Stop activity tracking
    activityService.stop();

    if ('caches' in window) {
      try {
        await caches.delete('cart-api');
      } catch (e) {
        console.error('[AuthContext] Error clearing cart cache:', e);
      }
    }
  }, [user, refreshToken]);

  // --- Initialize Auth ---
  const initializeAuth = useCallback(async (authToken, refreshTok, userData) => {
    logger.log("[AuthContext] Initializing auth...");
    setLoading(true);
    setCardPermsLoading(true);
    setRoutesLoaded(false);

    try {
      if (!userData || typeof userData !== 'object') {
        throw new Error("Invalid user data");
      }

      // Set auth state
      setToken(authToken);
      setRefreshToken(refreshTok);
      setUser(userData);
      setIsAuthenticated(true);
      
      // Set user roles
      const superuserStatus = userData?.is_superuser === true || userData?.is_superuser === 1;
      const userGroups = Array.isArray(userData?.groups) ? userData.groups : [];
      setUserRoles({ isSuperuser: superuserStatus, roles: userGroups });

      // Clear caches
      setPermissionCache({});
      setCardPermissions({});

      // Save to localStorage
      safeSetItem(AUTH_TOKEN_KEY, authToken);
      safeSetItem(AUTH_REFRESH_KEY, refreshTok);
      safeSetItem(USER_DATA_KEY, JSON.stringify(userData));

      // Fetch permissions concurrently
      logger.log("[AuthContext] Fetching permissions...");
      const [routesResult] = await Promise.allSettled([
        permissionService.getRoutes(),
        fetchAccess()
      ]);

      // Process routes
      if (routesResult.status === 'fulfilled' && routesResult.value?.success && routesResult.value?.data) {
        routeRegistry.registerRoutes(routesResult.value.data);
        setRoutesLoaded(true);
        logger.log("[AuthContext] Routes loaded successfully");
      } else {
        console.error("[AuthContext] Failed to load routes");
        setRoutesLoaded(false);
      }

      // Start activity tracking
      activityService.start();
      
      logger.log("[AuthContext] Auth initialization complete");
    } catch (error) {
      console.error("[AuthContext] Auth initialization error:", error);
      handleLogout();
    } finally {
      setLoading(false);
    }
  }, [handleLogout, fetchAccess]);

  // --- Login ---
  const login = useCallback(async (loginCredentials) => {
    logger.log("[AuthContext] Login started");
    setLoading(true);
    
    try {
      if (!loginCredentials?.username || !loginCredentials?.password) {
        throw new Error("Username and password required");
      }

      const response = await authService.login(loginCredentials.username, loginCredentials.password);

      if (response.success && response.data) {
        // Validate response
        const requiredFields = ['access_token', 'refresh_token', 'user_id', 'username'];
        for (const field of requiredFields) {
          if (!response.data[field]) {
            throw new Error(`Missing required field: ${field}`);
          }
        }

        const { access_token, refresh_token, ...userData } = response.data;

        // Initialize auth
        await initializeAuth(access_token, refresh_token, userData);
        logger.log("[AuthContext] Login successful");
        
        // Notify cart
        if (userData?.user_id) {
          notifyCartSystem('login', userData.user_id);
        }
        
        return { success: true };
      } else {
        throw new Error(response.error || "Invalid credentials");
      }
    } catch (error) {
      console.error("[AuthContext] Login error:", error);
      handleLogout();
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  }, [initializeAuth, handleLogout, notifyCartSystem]);

  // --- Switch User ---
  const switchUser = useCallback(async (authData) => {
    logger.log("[AuthContext] Switching user...");
    setLoading(true);

    try {
      if (!authData?.access_token || !authData?.refresh_token) {
        throw new Error("Invalid authentication data");
      }

      const previousUserId = user?.id;
      const { access_token, refresh_token, user: userData, ...rest } = authData;

      const userObject = userData || {
        user_id: rest.user_id,
        username: rest.username,
        is_superuser: rest.is_superuser,
        is_staff: rest.is_staff,
        groups: rest.groups,
        customer_profile: rest.customer_profile,
        default_picking_view: rest.default_picking_view,
        preferred_language: rest.preferred_language
      };

      authService.switchUser(authData);

      await initializeAuth(access_token, refresh_token, userObject);

      if (previousUserId && userObject?.user_id && previousUserId !== userObject.user_id) {
        notifyCartSystem('switch', userObject.user_id, previousUserId);
      }

      logger.log("[AuthContext] User switch successful");
      return { success: true };
    } catch (error) {
      console.error("[AuthContext] Switch user error:", error);
      return { success: false, error: error.message };
    } finally {
      setLoading(false);
    }
  }, [user, initializeAuth, notifyCartSystem]);

  // --- Auto login on mount ---
  useEffect(() => {
    if (!isInitialMount.current) return;
    isInitialMount.current = false;

    let cartNotificationTimer = null;

    const attemptAutoLogin = async () => {
      logger.log("[AuthContext] Attempting auto-login...");
      setLoading(true);

      const storedToken = localStorage.getItem(AUTH_TOKEN_KEY);
      const storedRefresh = localStorage.getItem(AUTH_REFRESH_KEY);
      const storedUserData = localStorage.getItem(USER_DATA_KEY);

      if (!storedToken || !storedUserData) {
        logger.log("[AuthContext] No stored credentials");
        handleLogout();
        setLoading(false);
        return;
      }

      try {
        const parsedUserData = JSON.parse(storedUserData);

        // Check token expiry
        let isExpired = false;
        try {
          const tokenData = jwtDecode(storedToken);
          isExpired = tokenData.exp * 1000 < Date.now();
        } catch {
          isExpired = true;
        }

        if (!isExpired) {
          logger.log("[AuthContext] Token valid, auto-logging in");
          await initializeAuth(storedToken, storedRefresh, parsedUserData);

          if (parsedUserData?.user_id) {
            cartNotificationTimer = setTimeout(() => notifyCartSystem('login', parsedUserData.user_id), 1000);
          }
        } else if (storedRefresh && navigator.onLine) {
          logger.log("[AuthContext] Token expired, refreshing...");
          const refreshed = await refreshTokenFunction();

          if (refreshed) {
            const newToken = localStorage.getItem(AUTH_TOKEN_KEY);
            await initializeAuth(newToken, storedRefresh, parsedUserData);

            if (parsedUserData?.user_id) {
              cartNotificationTimer = setTimeout(() => notifyCartSystem('login', parsedUserData.user_id), 1000);
            }
          } else {
            handleLogout();
          }
        } else {
          logger.log("[AuthContext] Cannot refresh token");
          handleLogout();
        }
      } catch (error) {
        console.error("[AuthContext] Auto-login error:", error);
        handleLogout();
      }
    };

    attemptAutoLogin();

    // Cleanup: Stop activity service and clear timers on unmount (HMR protection)
    return () => {
      logger.log("[AuthContext] Component unmounting, stopping activity service");
      activityService.stop();
      if (cartNotificationTimer) {
        clearTimeout(cartNotificationTimer);
      }
    };
  }, [initializeAuth, handleLogout, notifyCartSystem, refreshTokenFunction]);

  // --- Permission checks ---
  const effectiveAccess = preview ? preview.access : access;
  const effectiveSuperuser = preview ? false : userRoles.isSuperuser;

  const knownPaths = useMemo(
    () => (effectiveAccess ? [...new Set([...CATALOG_PATHS, ...effectiveAccess.pages])] : CATALOG_PATHS),
    [effectiveAccess]
  );

  const hasPagePermission = useCallback((pagePath) => {
    if (!isAuthenticated) return false;
    if (effectiveSuperuser) return true;
    if (!effectiveAccess) return false;
    const owner = matchRoute(pagePath, knownPaths);
    return owner !== null && effectiveAccess.pages.has(owner);
  }, [isAuthenticated, effectiveSuperuser, effectiveAccess, knownPaths]);

  const homePath = useMemo(() => {
    if (effectiveSuperuser || !effectiveAccess) return homeUrl(null);
    return homeUrl(effectiveAccess.home);
  }, [effectiveSuperuser, effectiveAccess]);

  // Batch permission check for performance
  const checkMultiplePermissions = useCallback((paths) => {
    if (!Array.isArray(paths)) return {};
    
    const results = {};
    paths.forEach(path => {
      results[path] = hasPagePermission(path);
    });
    
    return results;
  }, [hasPagePermission]);

  const hasCardPermission = useCallback((cardIdentifier) => {
    if (!isAuthenticated) return false;
    if (effectiveSuperuser) return true;
    return !!effectiveAccess && effectiveAccess.cards.has(cardIdentifier);
  }, [isAuthenticated, effectiveSuperuser, effectiveAccess]);

  // State of a UI element ("visible", "disabled", "hidden", "editable", "readonly") for the current user.
  const elementState = useCallback((key) => {
    if (effectiveSuperuser) return mostPermissive(key);
    if (!effectiveAccess) return 'hidden';
    return effectiveAccess.elements[key] || mostPermissive(key);
  }, [effectiveSuperuser, effectiveAccess]);

  // --- Enhanced permission methods ---
  const checkPermission = useCallback(async (permission, context = {}) => {
    if (!isAuthenticated) return false;
    if (userRoles.isSuperuser) return true;
    
    try {
      const result = await permissionService.checkPermission(permission, context);
      return result.success && result.hasPermission;
    } catch (error) {
      console.error('[AuthContext] Error checking permission:', error);
      return false;
    }
  }, [isAuthenticated, userRoles.isSuperuser]);

  const canPerformAction = useCallback(async (model, action, objectId = null) => {
    if (!isAuthenticated) return false;
    if (userRoles.isSuperuser) return true;
    
    try {
      const result = await permissionService.canPerformAction(model, action, objectId);
      return result.success && result.hasPermission;
    } catch (error) {
      console.error('[AuthContext] Error checking action permission:', error);
      return false;
    }
  }, [isAuthenticated, userRoles.isSuperuser]);

  const checkCustomerAccess = useCallback(async (customerId, action = 'view') => {
    if (!isAuthenticated) return false;
    if (userRoles.isSuperuser) return true;
    
    try {
      const result = await permissionService.checkCustomerAccess(customerId, action);
      return result.success && result.hasPermission;
    } catch (error) {
      console.error('[AuthContext] Error checking customer access:', error);
      return false;
    }
  }, [isAuthenticated, userRoles.isSuperuser]);

  const getUserPermissionsSummary = useCallback(async () => {
    if (!isAuthenticated) return null;
    
    try {
      const result = await permissionService.getUserPermissionsSummary();
      if (result.success) {
        return result.data;
      }
      return null;
    } catch (error) {
      console.error('[AuthContext] Error fetching permissions summary:', error);
      return null;
    }
  }, [isAuthenticated]);

  // Merge a few fields into the signed-in user without re-authenticating —
  // used by the profile page so the header reflects a renamed user at once.
  const updateUser = useCallback((changes) => {
    setUser(prev => (prev ? { ...prev, ...changes } : prev));
  }, []);

  // --- Context value ---
  const contextValue = {
    // Auth state
    user,
    token,
    loading,
    isAuthenticated,
    
    // Auth actions
    login,
    logout: handleLogout,
    switchUser,
    refreshToken: refreshTokenFunction,
    
    // Permissions
    hasPagePermission,
    checkMultiplePermissions,
    hasCardPermission,
    clearPermissionCache,
    refreshPermissions,
    elementState,
    homePath,
    access: effectiveAccess,
    preview,
    startPreview,
    stopPreview,
    accessLoaded: userRoles.isSuperuser || !!access,
    refreshAccess: reloadAccess,
    
    // Enhanced permissions
    checkPermission,
    canPerformAction,
    checkCustomerAccess,
    getUserPermissionsSummary,
    
    // User info
    isSuperuser: effectiveSuperuser,
    isRealSuperuser: userRoles.isSuperuser,
    userRoles: preview ? [preview.groupName] : userRoles.roles,
    
    // Loading states
    cardPermsLoading: userRoles.isSuperuser ? false : !access,
    routesLoaded,
    
    // Actions
    fetchCardPermissions,
    updateUser,
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};

// Custom hook for using the AuthContext
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export default AuthContext;